import { store } from "./store";
// "Remove ads" one-off purchase for the iPhone and Android apps (not used on the website).
//
// Uses the @capgo/native-purchases plugin through Capacitor's plugin registry, so the
// website build never loads native code.
//
// To switch on, after the app is live in both stores:
//   1. App Store Connect: In-App Purchases > Non-Consumable, product ID "rafiki_remove_ads".
//   2. Google Play Console: Monetise > Products > In-app products, same product ID.
//   3. Amazon Appstore (Fire tablets, no Google Play Billing): In-App Items > Entitlement, SKU
//      "rafiki_remove_ads", and the app's AppstoreAuthenticationKey.pem in the Android project's
//      app/src/main/assets (see app/android/README-amazon.md).
//   4. Set REMOVE_ADS_ENABLED = true and rebuild the apps.
//
// Which store: iOS uses the App Store; Android asks the app's StoreInfo plugin who installed it.
// An Amazon install (installer com.amazon.venezia, or a Fire device) buys through the app's
// AmazonIap plugin (Amazon Appstore SDK); any other Android install through Google Play.

const capacitor = (): any => (window as any).Capacitor;
const isNativeApp = (): boolean => Boolean(capacitor()?.isNativePlatform?.());

export const REMOVE_ADS_ENABLED = false;
export const REMOVE_ADS_PRODUCT_ID = "rafiki_remove_ads";
// Amazon's SKU for the same entitlement.
export const AMAZON_REMOVE_ADS_SKU = "rafiki_remove_ads";
const STORAGE_KEY = "rafiki_ad_free";

// Rafiki's own Android plugins (app/android/.../StoreInfoPlugin.java, AmazonIapPlugin.java).
const localPlugin = (name: string): any => {
  try {
    const cap = capacitor();
    return cap?.Plugins?.[name] || cap?.registerPlugin?.(name);
  } catch (e) {
    return undefined;
  }
};

export type StoreKind = "apple" | "google" | "amazon" | "none";

export const isAmazonInstall = (info: { installer?: string; manufacturer?: string } | undefined): boolean =>
  String(info?.installer || "") === "com.amazon.venezia" || String(info?.manufacturer || "").trim().toLowerCase() === "amazon";

let storeKind: StoreKind | undefined;
let detecting: Promise<StoreKind> | null = null;

// Work out which store this app came from (once). Web and iOS never call the Android plugin.
export const detectStore = (): Promise<StoreKind> => {
  if (storeKind) return Promise.resolve(storeKind);
  if (detecting) return detecting;
  detecting = (async (): Promise<StoreKind> => {
    let kind: StoreKind = "none";
    if (isNativeApp()) {
      if (capacitor()?.getPlatform?.() === "ios") kind = "apple";
      else {
        kind = "google";
        try {
          const info = await localPlugin("StoreInfo")?.getInfo?.();
          if (isAmazonInstall(info)) kind = "amazon";
        } catch (e) {
          // an older app without the plugin: Google Play, as before
        }
      }
    }
    storeKind = kind;
    detecting = null;
    return kind;
  })();
  return detecting;
};

let enabledForTests: boolean | undefined;
const enabled = () => (enabledForTests === undefined ? REMOVE_ADS_ENABLED : enabledForTests);

// Tests only: forget the detected store, and switch the (constant) flag.
export const _resetStoreForTests = (enabledOverride?: boolean) => {
  storeKind = undefined;
  detecting = null;
  enabledForTests = enabledOverride;
};

const plugin = () => capacitor()?.Plugins?.NativePurchases;
const amazon = () => localPlugin("AmazonIap");
const onAmazon = () => storeKind === "amazon";
const listeners: Array<(adFree: boolean) => void> = [];

const readFlag = (): boolean => {
  try {
    return store.getItem(STORAGE_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const writeFlag = (adFree: boolean) => {
  try {
    if (adFree) store.setItem(STORAGE_KEY, "1");
    else store.removeItem(STORAGE_KEY);
  } catch (e) {
    // storage unavailable: the store check on next launch restores it
  }
  listeners.forEach((l) => l(adFree));
};

// Until the store is known (a moment after launch on Android) the option stays hidden.
export const removeAdsAvailable = (): boolean => {
  if (!enabled() || !isNativeApp() || !storeKind || storeKind === "none") return false;
  return onAmazon() ? Boolean(amazon()) : Boolean(plugin());
};

// Resolves once removeAdsAvailable() can give its final answer.
export const removeAdsReady = async (): Promise<boolean> => {
  if (!enabled() || !isNativeApp()) return false;
  await detectStore();
  return removeAdsAvailable();
};

export const isAdFree = (): boolean => removeAdsAvailable() && readFlag();

export const onAdFreeChange = (listener: (adFree: boolean) => void) => {
  listeners.push(listener);
  return () => {
    const i = listeners.indexOf(listener);
    if (i >= 0) listeners.splice(i, 1);
  };
};

const ownsRemoveAds = (purchases: any[] = []): boolean =>
  purchases.some(
    (p) =>
      p?.productIdentifier === REMOVE_ADS_PRODUCT_ID &&
      // Android reports pending purchases (e.g. awaiting cash payment); only count completed ones.
      String(p?.purchaseState ?? "PURCHASED").toUpperCase() !== "PENDING" &&
      String(p?.purchaseState ?? "1") !== "2"
  );

// Ask the store what this account owns. Called at app start, so a reinstall or a refund is picked up.
export const refreshAdFree = async (): Promise<boolean> => {
  if (!(await removeAdsReady())) return false;
  try {
    if (onAmazon()) {
      const { skus } = (await amazon().restore()) || {};
      const owned = Array.isArray(skus) && skus.includes(AMAZON_REMOVE_ADS_SKU);
      writeFlag(owned);
      return owned;
    }
    const { purchases } = await plugin().getPurchases({ productType: "inapp" });
    const owned = ownsRemoveAds(purchases);
    writeFlag(owned);
    return owned;
  } catch (e) {
    // Offline or store unavailable: keep the last known answer.
    return readFlag();
  }
};

export const getRemoveAdsPrice = async (): Promise<string | undefined> => {
  if (!(await removeAdsReady())) return undefined;
  try {
    if (onAmazon()) {
      const product = await amazon().getProduct({ sku: AMAZON_REMOVE_ADS_SKU });
      return product?.price || undefined;
    }
    const { products } = await plugin().getProducts({
      productIdentifiers: [REMOVE_ADS_PRODUCT_ID],
      productType: "inapp",
    });
    return products?.[0]?.priceString;
  } catch (e) {
    return undefined;
  }
};

export const buyRemoveAds = async (): Promise<boolean> => {
  if (!(await removeAdsReady())) return false;
  if (onAmazon()) {
    const result = await amazon().purchase({ sku: AMAZON_REMOVE_ADS_SKU });
    const owned = result?.owned === true ? true : await refreshAdFree();
    writeFlag(owned);
    return owned;
  }
  const tx = await plugin().purchaseProduct({
    productIdentifier: REMOVE_ADS_PRODUCT_ID,
    productType: "inapp",
    isConsumable: false,
  });
  const owned = tx?.productIdentifier === REMOVE_ADS_PRODUCT_ID ? true : await refreshAdFree();
  writeFlag(owned);
  return owned;
};

// Apple requires a "Restore purchases" option for one-off purchases.
export const restoreRemoveAds = async (): Promise<boolean> => {
  if (!(await removeAdsReady())) return false;
  // Amazon: refreshAdFree asks Amazon for the full purchase history (getPurchaseUpdates).
  if (onAmazon()) return refreshAdFree();
  try {
    await plugin().restorePurchases();
  } catch (e) {
    // fall through to the entitlement check
  }
  return refreshAdFree();
};
